import {ComponentType} from '@angular/cdk/portal';
import {Compiler, Injectable, Injector, NgModuleFactory, Type} from '@angular/core';
import {Reducers} from '@app/reducers';
import {AssociationViewDialogComponent} from '@clients/components/associations/association-view-dialog/association-view-dialog.component';
import {ConfigService} from '@config/config.service';
import {ContactDialogComponent} from '@crm/components/contacts/contact-dialog/contact-dialog.component';
import {CrmTaskDialogComponent} from '@crm/components/leads/crm-task-dialog/crm-task-dialog.component';
import {ProjectDialogComponent} from '@crm/components/projects/project-dialog/project-dialog.component';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {AssociationListResponse} from '@services/back-office/model/associationListResponse';
import {ContactResponse} from '@services/associations/model/contactResponse';
import {ProjectResponse} from '@services/associations/model/projectResponse';
import {TaskResponse} from '@services/associations/model/taskResponse';
import {ListItemType} from '@shared/enum/list-item-type';
import {EnumToObjectModel} from '@shared/models/enum-to-object';
import {MinimizedDialogInfoModel} from '@shared/models/minimized-dialog-info.model';
import {AssociationsService} from '@shared/services/associations.service';
import {CrmService} from '@shared/services/crm.service';
import {ToastrService} from 'ngx-toastr';
import {catchError, EMPTY, first, firstValueFrom, fromEvent, merge, Observable} from 'rxjs';
import {filter, take, takeUntil} from 'rxjs/operators';
import * as mainActions from '@main/ngrx/actions/main.actions';
import {ContractDialogComponent} from "@billing/components/contracts/contract-dialog/contract-dialog.component";
import {BillingService} from "@shared/services/billing.service";
import {BillingContractModel} from "@services/back-office/model/billingContractModel";
import {BillingCounterpartyModel} from "@services/back-office/model/billingCounterpartyModel";
import {CounterpartyDialogComponent} from "@billing/components/counterparty/counterparty-dialog/counterparty-dialog.component";
import {PaymentDialogComponent} from "@billing/components/payments/payment-dialog/payment-dialog.component";
import {RoleModel} from "@services/back-office/model/roleModel";
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import PermissionEnum = RoleModel.PermissionsEnum;

@Injectable({
    providedIn: 'root'
})
export class SharedService {
    /** Ширина вікна в px, менше якої діалог з fullscreenOnNarrow займає все вікно замість #main-wrapper. */
    private static readonly MAIN_WRAPPER_FULLSCREEN_BELOW = 1400;

    constructor(
        public dialog: MatDialog,
        private crmService: CrmService,
        private billingService: BillingService,
        private associationService: AssociationsService,
        private config: ConfigService,
        private compiler: Compiler,
        private injector: Injector,
    ) {
    }

    /** Transform data to permissions */
    public static transformDataToPermissions(
        hexStringWithAssociations: { association: string, hex: string, bp?: string[] }[],
        permissionsNumbers: { hex: string, permissions: number[] },
    ): { targetId: string, reference: string, buildingPermissions: number[], permissions: number[], permissionsExists: boolean }[] {
        const result = [];

        for (const hexStringWithAssociation of hexStringWithAssociations) {
            const resultItem = {
                targetId: hexStringWithAssociation.association,
                reference: 'ASSOCIATION',
                ...hexStringWithAssociation && hexStringWithAssociation.bp && {buildingPermissions: hexStringWithAssociation.bp},
                // @ts-ignore
                permissions: permissionsNumbers[hexStringWithAssociation.hex],
                permissionsExists: true,
            };

            result.push(resultItem);
        }
        // @ts-ignore
        return result;
    }

    /** Check Permission */
    public static checkPermission(permissionName: string, userPermissions: number[], permissionsExists: boolean): boolean {
        /** Get all Permissions from ENUM */
        const permissionsArray: string[] = [];

        /** Map Enum to Array */
        Object.keys(PermissionEnum).map(key => {
            // @ts-ignore
            permissionsArray.push(PermissionEnum[key]);
        });

        /** Find index of permission name */
        const permissionNumber = permissionsArray.indexOf(permissionName);

        return userPermissions ?
            (
                permissionsExists ?
                    userPermissions.includes(permissionNumber) :
                    (permissionNumber !== -1 ? !userPermissions.includes(permissionNumber) : true)
            ) : true;
    }

    public static checkAllPermissions(userPermissions: number[]): boolean {
        return userPermissions.length == Object.keys(PermissionEnum).length;
    }

    /** Check Route permissions */
    public static checkPermissionsMultiple(queryPermissionName: string[], userPermissions: number[], permissionsExists: boolean): boolean {
        /** Get all Permissions from ENUM */
        const permissionsArray: string[] = [];
        /** Get User permissions names (instead of numbers) */
        const userPermissionNames: string[] = [];

        /** Map Enum to Array */
        Object.keys(PermissionEnum).map(key => {
            // @ts-ignore
            permissionsArray.push(PermissionEnum[key]);
        });

        /** Get User permissions Names from User permissions numbers array */
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Object.entries(userPermissions).forEach(([, value]) => {
            userPermissionNames.push(permissionsArray[value]);
        });

        return userPermissionNames && userPermissionNames.length ?
            (
                permissionsExists ?
                    userPermissionNames.some(userPermissionsNameItem => queryPermissionName.includes(userPermissionsNameItem)) :
                    !this.arrayValuesChecker(userPermissionNames, queryPermissionName)
            ) : true;
    }

    public static showSuccessWithTitle(
        translate: TranslateService,
        toastrService: ToastrService,
        translationKey: string,
        interpolateParams?: any,
    ) {
        let titleTranslation = '';
        let textTranslation = '';

        translate.stream(`${translationKey}.title`, interpolateParams).pipe(first()).subscribe(res => {
            titleTranslation = res;
        });

        translate.stream(`${translationKey}.text`, interpolateParams).pipe(first()).subscribe(res => {
            textTranslation = res;
        });

        toastrService.success(textTranslation.length ? textTranslation : '', titleTranslation);
    }

    public static showErrorWithTitle(
        translate: TranslateService,
        toastrService: ToastrService,
        translationKey: string,
        interpolations?: Record<string, unknown>
    ) {
        let titleTranslation = '';
        let textTranslation = '';

        translate.stream(`${translationKey}.title`, interpolations).pipe(first()).subscribe((res: string) => {
            titleTranslation = res;
        });

        translate.stream(`${translationKey}.text`, interpolations).pipe(first()).subscribe((res: string) => {
            textTranslation = res;
        });

        toastrService.error(textTranslation.length ? textTranslation : '', titleTranslation);
    }

    public static showWarningWithTitle(
        translate: TranslateService,
        toastrService: ToastrService,
        translationKey: string,
        interpolateParams?: any,
    ) {
        let titleTranslation = '';
        let textTranslation = '';

        translate.stream(`${translationKey}.title`, interpolateParams).pipe(first()).subscribe(res => {
            titleTranslation = res;
        });

        translate.stream(`${translationKey}.text`, interpolateParams).pipe(first()).subscribe(res => {
            textTranslation = res;
        });

        toastrService.warning(textTranslation.length ? textTranslation : '', titleTranslation);
    }

    /** Translate */
    public static getTranslation(translateService: any, key: string, interpolations?: Record<string, unknown>) {
        let translation: string;
        translateService.stream(key, interpolations)
            .pipe(first())
            .subscribe((res: any) => {
                translation = res;
            });

        // @ts-ignore
        return translation;
    }

    /** Convert Enum to Object */
    public static convertEnumToObject(item: any): EnumToObjectModel[] {
        return Object.keys(item).map(key => ({
            id: item[key],
            value: item[key]
        }));
    }

    /** 'Minimize' Dialog (make it hidden) */
    public static minimize(
        dialogId: string,
        dialogTitle: string,
        store$: Store<Reducers>,
        referenceId?: string,
        doNotShowInToolbar?: boolean,
        customName?: string,
        dialogTooltip?: string,
    ) {
        let dialogObject = null;
        let dialogWrapper = null;

        if (dialogId) {
            dialogObject = document.getElementById(dialogId);
            if (dialogObject) {
                dialogWrapper = dialogObject.closest('.cdk-global-overlay-wrapper');
            }
        }

        if (dialogWrapper) {
            // @ts-ignore
            dialogWrapper.style.display = 'none';
            store$.dispatch(new mainActions.MinimizeDialog({
                dialogId,
                dialogName: dialogTitle,
                active: false,
                restored: false,
                referenceId: referenceId ? referenceId : undefined,
                doNotShowInToolbar: !!doNotShowInToolbar,
                customName: customName ? customName : undefined,
                dialogTooltip: dialogTooltip ? dialogTooltip : undefined,
            }));
        }
    }

    static formatPhoneNumber(rawNumber: string): string {
        if (rawNumber && rawNumber.length == 13) {
            return rawNumber;
        }

        if (rawNumber && rawNumber.length == 12) {
            return `+${rawNumber.slice(0, rawNumber.length)}`;
        }

        if (rawNumber && rawNumber.length == 11) {
            return `+3${rawNumber.slice(0, rawNumber.length)}`;
        }

        if (rawNumber && rawNumber.length == 10) {
            return `+38${rawNumber.slice(0, rawNumber.length)}`;
        }

        if (rawNumber && rawNumber.length == 9) {
            return `+380${rawNumber.slice(0, rawNumber.length)}`;
        }

        return rawNumber;
    }

    /** Check If Array contains all elements of another array */
    private static arrayValuesChecker(arr: any[], target: any[]): boolean {
        return target.every(value => arr.includes(value));
    }

    /** Open a Dialog */
    openDialog(component: ComponentType<unknown>, config: MatDialogConfig<any> | undefined, dialogData?: any) {
        return this.dialog.open(component, {
            ...config,
            data: dialogData,
        });
    }

    /** Відкриває діалог точно поверх #main-wrapper (content-container у main.container.component.html), а не на все вікно,
     *  і стежить за ним, коли той змінює розмір чи зсувається.
     *  fullscreenOnNarrow — на вікні, вужчому за MAIN_WRAPPER_FULLSCREEN_BELOW, діалог займає всю ширину й висоту вікна замість #main-wrapper. */
    openMainWrapperDialog(component: ComponentType<unknown>, dialogData?: any, fullscreenOnNarrow = false): MatDialogRef<unknown> {
        const dialogRef = this.openDialog(component, this.getMainWrapperDialogConfig(fullscreenOnNarrow), dialogData);
        const wrapper = document.getElementById('main-wrapper');

        /** Сам resize вікна пропускає зміну розміру wrapper'а без зміни вікна, а сам ResizeObserver — його зсув без зміни розміру. */
        const wrapperResize$ = new Observable<void>(subscriber => {
            if (!wrapper) {
                return;
            }

            const observer = new ResizeObserver(() => subscriber.next());
            observer.observe(wrapper);

            return () => observer.disconnect();
        });

        merge(fromEvent(window, 'resize'), wrapperResize$).pipe(
            takeUntil(dialogRef.afterClosed())
        ).subscribe(() => this.applyMainWrapperBounds(dialogRef, fullscreenOnNarrow));

        return dialogRef;
    }

    /** Межі, які має займати діалог: #main-wrapper, або все вікно з відступом 10px з кожного боку, коли воно вужче за MAIN_WRAPPER_FULLSCREEN_BELOW і задано fullscreenOnNarrow. */
    private getMainWrapperBounds(fullscreenOnNarrow: boolean): {top: number, left: number, width: number, height: number} | undefined {
        if (fullscreenOnNarrow && window.innerWidth < SharedService.MAIN_WRAPPER_FULLSCREEN_BELOW) {
            const margin = 10;
            return {top: margin, left: margin, width: window.innerWidth - margin * 2, height: window.innerHeight - margin * 2};
        }

        return document.getElementById('main-wrapper')?.getBoundingClientRect();
    }

    private getMainWrapperDialogConfig(fullscreenOnNarrow: boolean): MatDialogConfig {
        const rect = this.getMainWrapperBounds(fullscreenOnNarrow);

        return {
            ...this.config.wideDialog,
            hasBackdrop: true,
            width: rect ? `${rect.width}px` : '100%',
            height: rect ? `${rect.height}px` : '100%',
            /** Не розмір wrapper'а на момент відкриття: updateSize() не змінює max-*, і вони не дали б діалогу знову розширитись. */
            maxWidth: rect ? '100vw' : this.config.wideDialog.maxWidth,
            maxHeight: rect ? '100vh' : this.config.wideDialog.maxHeight,
            position: rect ? {top: `${rect.top}px`, left: `${rect.left}px`} : undefined,
        };
    }

    private applyMainWrapperBounds(dialogRef: MatDialogRef<unknown>, fullscreenOnNarrow: boolean) {
        const rect = this.getMainWrapperBounds(fullscreenOnNarrow);
        if (!rect) {
            return;
        }

        dialogRef.updateSize(`${rect.width}px`, `${rect.height}px`);
        dialogRef.updatePosition({top: `${rect.top}px`, left: `${rect.left}px`});
    }

    /** Restore Dialog */
    restoreDialog(dialogId: string, minimizedDialogs: MinimizedDialogInfoModel[], store$: Store) {
        const dialogElement = document.getElementById(dialogId);
        const dialogItem = minimizedDialogs && minimizedDialogs.length ?
            minimizedDialogs.find((item: MinimizedDialogInfoModel) => item.dialogId === dialogId) :
            null;

        if (dialogElement && dialogItem) {
            const dialogWrapper = dialogElement.closest('.cdk-global-overlay-wrapper');
            if (dialogWrapper) {
                // @ts-ignore
                dialogWrapper.style.display = 'flex';

                /** Make dialog on top */
                const dialogOverlays = document.getElementsByClassName('cdk-global-overlay-wrapper');
                if (dialogOverlays && dialogOverlays.length) {
                    for (const index in dialogOverlays) {
                        // eslint-disable-next-line no-prototype-builtins
                        if (dialogOverlays.hasOwnProperty(index)) {
                            // @ts-ignore
                            dialogOverlays[index].style.zIndex = '999';
                        }
                    }
                }

                // @ts-ignore
                dialogWrapper.style.zIndex = '1000';
                store$.dispatch(new mainActions.RestoreMinimizedDialog(dialogId));
            }
        }
    }

    createCrmItem(dataType: string, data?: any, lazy?: boolean) {
        if (dataType === ListItemType.TASK) {
            this.openDialog(
                CrmTaskDialogComponent,
                {...this.config.rightSideDialog, ...this.config.canMinimizeDialog},
                {contact: data}
            );
        }

        if (dataType === ListItemType.CONTACT) {
            this.openDialog(
                ContactDialogComponent,
                {...this.config.rightSideDialog, ...this.config.canMinimizeDialog},
                {phoneNumber: data}
            );
        }

        if (dataType === ListItemType.PROJECT) {
            this.openDialog(
                ProjectDialogComponent,
                {...this.config.rightSideDialog, ...this.config.canMinimizeDialog}
            );
        }

        if (dataType === ListItemType.COUNTERPARTY) {
            if (lazy) {
                this.loadLazyModule(() => {
                    return import('@app/billing/billing-v2.module').then(mod => mod.BillingV2Module);
                });
            }

            this.openDialog(
                CounterpartyDialogComponent,
                {...this.config.addItemDialog, hasBackdrop: true},
                {}
            );
        }

        if (dataType === ListItemType.CONTRACT) {
            if (lazy) {
                this.loadLazyModule(() =>
                    import('@app/billing/billing-v2.module').then(mod => mod.BillingV2Module)
                ).then(() => {
                    this.openDialog(
                        ContractDialogComponent,
                        {...this.config.contractDialog, ...this.config.canMinimizeDialog},
                        {...data}
                    );
                });
            }
        }
    }

    async openAssociationItemAsync(itemId: string, props?: any): Promise<boolean> {
        try {
            await this.loadLazyModule(
                async () => (await import('../clients/clients.module')).ClientsModule
            );

            const association = await firstValueFrom(
                this.associationService.getAssociation(itemId).pipe(
                    filter(Boolean),
                    take(1),
                    catchError(() => EMPTY)
                )
            );

            if (!association) return false;

            this.openDialog(
                AssociationViewDialogComponent,
                { ...this.config.viewItemDialog, hasBackdrop: true },
                { ...props, edit: true, association }
            );

            return true;
        } catch (e) {
            return false;
        }
    }

    /** Відкриває діалог CRM-сутності; Promise завершується, коли діалог відкрито (або при помилці). */
    async openCrmItem(dataType: string, itemId: string, props?: any): Promise<void> {
        try {
            if (dataType === ListItemType.CONTACT) {
                await this.loadLazyModule(async () => {
                    const mod = await import('../crm/crm.module');
                    return mod.CrmModule;
                });
                const contact: ContactResponse | null = await firstValueFrom(
                    this.crmService.getContact(itemId).pipe(filter(data => !!data)),
                    {defaultValue: null}
                );
                if (contact) {
                    this.openDialog(
                        ContactDialogComponent,
                        {...this.config.rightSideDialog, ...this.config.canMinimizeDialog},
                        {edit: true, contactToEdit: contact}
                    );
                }
            }

            if (dataType === ListItemType.PROJECT) {
                const project: ProjectResponse | null = await firstValueFrom(
                    this.crmService.getProjects(itemId).pipe(filter(data => !!data)),
                    {defaultValue: null}
                );
                if (project) {
                    this.openDialog(
                        ProjectDialogComponent,
                        {...this.config.rightSideDialog, ...this.config.canMinimizeDialog},
                        {edit: true, projectToEdit: project}
                    );
                }
            }

            if (dataType === ListItemType.TASK) {
                const task: TaskResponse | null = await firstValueFrom(
                    this.crmService.getTask(itemId).pipe(filter(data => !!data)),
                    {defaultValue: null}
                );
                if (task) {
                    this.openDialog(
                        CrmTaskDialogComponent,
                        {...this.config.rightSideDialog, ...this.config.canMinimizeDialog},
                        {edit: true, task}
                    );
                }
            }

            if (dataType === ListItemType.ASSOCIATION) {
                await this.loadLazyModule(async () => {
                    const mod = await import('../clients/clients.module');
                    return mod.ClientsModule;
                });
                const association: AssociationListResponse | null = await firstValueFrom(
                    this.associationService.getAssociation(itemId).pipe(filter(data => !!data)),
                    {defaultValue: null}
                );
                if (association) {
                    this.openDialog(
                        AssociationViewDialogComponent,
                        {...this.config.viewItemDialog, hasBackdrop: true},
                        {...props, edit: true, association}
                    );
                }
            }

            if (dataType === ListItemType.CONTRACT) {
                await this.loadLazyModule(async () => {
                    const mod = await import('@app/billing/billing-v2.module');
                    return mod.BillingV2Module;
                });
                const contract: BillingContractModel | null = await firstValueFrom(
                    this.billingService.getContract(itemId).pipe(filter(data => !!data)),
                    {defaultValue: null}
                );
                if (contract) {
                    this.openDialog(
                        ContractDialogComponent,
                        {...this.config.contractDialog, ...this.config.canMinimizeDialog},
                        {edit: true, contract}
                    );
                }
            }

            if (dataType === ListItemType.TRANSACTION) {
                await this.loadLazyModule(async () => {
                    const mod = await import('@app/billing/billing-v2.module');
                    return mod.BillingV2Module;
                });
                this.openDialog(
                    PaymentDialogComponent,
                    {...this.config.viewBillingDocDialog, ...this.config.canMinimizeDialog},
                    {payment: {id: itemId}}
                );
            }

            if (dataType === ListItemType.COUNTERPARTY) {
                await this.loadLazyModule(async () => {
                    const mod = await import('@app/billing/billing-v2.module');
                    return mod.BillingV2Module;
                });
                const counterparty: BillingCounterpartyModel | null = await firstValueFrom(
                    this.billingService.getCounterparty(itemId).pipe(filter(data => !!data)),
                    {defaultValue: null}
                );
                if (counterparty) {
                    this.openDialog(
                        CounterpartyDialogComponent,
                        {...this.config.addItemDialog, hasBackdrop: true},
                        {...props, counterparty}
                    );
                }
            }
        } catch (e) {
            // помилки завантаження ігноруємо, як і раніше
        }
    }

    /**
     * Load Lazy Module, for instance, to use Actions of not loaded Module
     * https://juristr.com/blog/2019/10/lazyload-module-ivy-viewengine/
     */
    async loadLazyModule(path: any): Promise<any> {
        return (path() as Promise<NgModuleFactory<any> | Type<any>>)
            .then(elementModuleOrFactory => {
                if (elementModuleOrFactory instanceof NgModuleFactory) {
                    // if ViewEngine
                    return elementModuleOrFactory;
                } else {
                    // eslint-disable-next-line no-useless-catch
                    try {
                        // if Ivy
                        return this.compiler.compileModuleAsync(elementModuleOrFactory);
                    } catch (err) {
                        throw err;
                    }
                }
            })
            .then(moduleFactory => {
                // eslint-disable-next-line no-useless-catch
                try {
                    const elementModuleRef = moduleFactory.create(this.injector);
                    return elementModuleRef.instance;
                } catch (err) {
                    throw err;
                }
            });
    }
}
